import { api } from '@verifime/api-definition';
import { OPTED_IN_DOCUMENT_TYPES_COPY, TOrganisationCodeWorkflowType } from '@verifime/components';
import { useEffect, useState } from 'react';
import { TNotificationHandler } from '../../../../types';

// The saved list, typed as the generated organisation code response types it. It is taken from
// that response rather than named, so it follows the contract whichever enum the contract uses
// for the field.
type TSavedOptedInDocumentTypes = NonNullable<
  Awaited<
    ReturnType<typeof api.getV1organisationOrganisationIdcode>
  >[number]['optedInDocumentTypes']
>;

// The list the save sends, typed as the generated PUT request types it, so the write path follows
// the contract on its own and not through the read type.
type TOptedInDocumentTypesBody = NonNullable<
  Parameters<
    typeof api.putV1organisationcodeOrganisationCodeoptedInDocumentTypes
  >[0]['optedInDocumentTypes']
>;

// The opt-in types the admin picker offers, in the order it shows them. Plain literals, checked
// against the PUT request type: a value the contract drops stops compiling here. A value the
// contract adds is not offered until it is listed here.
const AVAILABLE_OPTED_IN_DOCUMENT_TYPES = [
  'PhotoCard',
  'ImmiCard',
] as const satisfies readonly TOptedInDocumentTypesBody[number][];
type TAvailableOptedInDocumentType = (typeof AVAILABLE_OPTED_IN_DOCUMENT_TYPES)[number];

// The reason the backend gave for a refused request, when it gave one (design intent D4). The
// Zodios auth interceptor re-throws 4xx as the raw AxiosError, whose `.message` is the generic
// "Request failed with status code 400"; the backend body is in `response.data`.
// Precedence: (1) a non-empty string `response.data`; else (2) a meaningful Error.message that is
// not the generic axios string; else undefined, and the caller uses its own fixed message.
const GENERIC_AXIOS_MESSAGE = /request failed with status code/i;

function bodyReason(err: unknown): string | undefined {
  const data = (err as { response?: { data?: unknown } })?.response?.data;
  return typeof data === 'string' && data.trim() ? data : undefined;
}

function errorMessageReason(err: unknown): string | undefined {
  if (!(err instanceof Error) || !err.message) {
    return undefined;
  }
  return GENERIC_AXIOS_MESSAGE.test(err.message) ? undefined : err.message;
}

function backendReason(err: unknown): string | undefined {
  return bodyReason(err) ?? errorMessageReason(err);
}

export interface OrganisationCodeData {
  code: string;
  workflow: TOrganisationCodeWorkflowType;
  returnUrl: string;
  redirectAllowedOrigins: string[];
  optedInDocumentTypes: TSavedOptedInDocumentTypes;
}

export interface UseOrganisationCodeSettingsResult {
  selectedCode: string;
  setSelectedCode: (code: string) => void;
  isLoading: boolean;
  organisationCodes: string[];
  selectedWorkflow: TOrganisationCodeWorkflowType;
  disabledWorkflows: Record<TOrganisationCodeWorkflowType, boolean>;
  handleWorkflowSwitch: (workflow: TOrganisationCodeWorkflowType) => Promise<void>;
  initialReturnUrl: string;
  handleReturnUrlSave: (returnUrl: string) => Promise<void>;
  initialRedirectAllowedOrigins: string[];
  handleAllowedOriginsSave: (allowedOrigins: string[]) => Promise<void>;
  availableOptedInDocumentTypes: readonly TAvailableOptedInDocumentType[];
  initialOptedInDocumentTypes: TSavedOptedInDocumentTypes;
  handleOptedInDocumentTypesSave: (documentTypes: TOptedInDocumentTypesBody) => Promise<void>;
}

export function useOrganisationCodeSettings(
  organisationId: string,
  successNotificationHandler: TNotificationHandler = console.log,
  errorNotificationHandler: TNotificationHandler = console.error,
): UseOrganisationCodeSettingsResult {
  const [selectedCode, setSelectedCode] = useState<string>('');
  const [isLoading, setIsLoading] = useState(true);
  const [codeDataMap, setCodeDataMap] = useState<Map<string, OrganisationCodeData>>(new Map());

  // Fetch organisation codes and their data
  useEffect(() => {
    setIsLoading(true);
    api
      .getV1organisationOrganisationIdcode({
        params: { organisationId },
      })
      .then((data) => {
        const mapping = new Map<string, OrganisationCodeData>();
        data.forEach((item) => {
          if (item.code) {
            mapping.set(item.code, {
              code: item.code,
              workflow: item.onboardingWorkflowType,
              returnUrl: item.returnUrl || '',
              redirectAllowedOrigins: item.redirectAllowedOrigins || [],
              optedInDocumentTypes: item.optedInDocumentTypes || [],
            });
          }
        });
        setCodeDataMap(mapping);
        // Set the first code as default selection
        if (mapping.size > 0) {
          setSelectedCode(Array.from(mapping.keys())[0]);
        }
      })
      .catch((err) => {
        errorNotificationHandler(err instanceof Error ? err.message : 'An unknown error occurred');
        console.error('Error fetching organisation codes:', err);
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [organisationId, errorNotificationHandler]);

  const organisationCodes = Array.from(codeDataMap.keys());
  const currentCodeData = selectedCode ? codeDataMap.get(selectedCode) : undefined;
  const selectedWorkflow = currentCodeData?.workflow || 'DEFAULT';
  const initialReturnUrl = currentCodeData?.returnUrl || '';
  const initialRedirectAllowedOrigins = currentCodeData?.redirectAllowedOrigins || [];
  const initialOptedInDocumentTypes = currentCodeData?.optedInDocumentTypes || [];

  // Calculate disabled workflows based on current workflow
  const disabledWorkflows: Record<TOrganisationCodeWorkflowType, boolean> = {
    DEFAULT: false,
    INDIVIDUAL_ONLY: selectedWorkflow === 'ORGANISATION_ONLY',
    ORGANISATION_ONLY: selectedWorkflow === 'INDIVIDUAL_ONLY',
  };

  const handleWorkflowSwitch = async (workflow: TOrganisationCodeWorkflowType) => {
    if (!selectedCode) {
      return;
    }

    try {
      await api.putV1organisationcodeOrganisationCodeworkflow(
        { workflowType: workflow },
        { params: { organisationCode: selectedCode } },
      );

      // Update local state
      setCodeDataMap((prevMap) => {
        const newMap = new Map(prevMap);
        const existingData = newMap.get(selectedCode);
        if (existingData) {
          newMap.set(selectedCode, {
            ...existingData,
            workflow,
          });
        }
        return newMap;
      });

      successNotificationHandler('Workflow updated successfully');
    } catch (err) {
      errorNotificationHandler(err instanceof Error ? err.message : 'Failed to update workflow');
      throw err;
    }
  };

  const handleReturnUrlSave = async (returnUrl: string) => {
    if (!selectedCode) {
      return;
    }

    try {
      await api.putV1organisationcodeOrganisationCodereturnUrl(
        { returnUrl: returnUrl || null },
        { params: { organisationCode: selectedCode } },
      );

      // Update local state
      setCodeDataMap((prevMap) => {
        const newMap = new Map(prevMap);
        const existingData = newMap.get(selectedCode);
        if (existingData) {
          newMap.set(selectedCode, {
            ...existingData,
            returnUrl,
          });
        }
        return newMap;
      });

      successNotificationHandler('Return URL updated successfully');
    } catch (err) {
      errorNotificationHandler(err instanceof Error ? err.message : 'Failed to update return URL');
      throw err;
    }
  };

  const handleAllowedOriginsSave = async (allowedOrigins: string[]) => {
    if (!selectedCode) {
      return;
    }

    try {
      await api.putV1organisationcodeOrganisationCoderedirectAllowedOrigins(
        { allowedOrigins: allowedOrigins.length ? allowedOrigins : null },
        { params: { organisationCode: selectedCode } },
      );

      // Update local state
      setCodeDataMap((prevMap) => {
        const newMap = new Map(prevMap);
        const existingData = newMap.get(selectedCode);
        if (existingData) {
          newMap.set(selectedCode, {
            ...existingData,
            redirectAllowedOrigins: allowedOrigins,
          });
        }
        return newMap;
      });

      successNotificationHandler('Allowed origins updated successfully');
    } catch (err) {
      const message = backendReason(err) ?? 'Failed to update allowed origins';
      errorNotificationHandler(message);
      throw new Error(message);
    }
  };

  const handleOptedInDocumentTypesSave = async (documentTypes: TOptedInDocumentTypesBody) => {
    if (!selectedCode) {
      return;
    }

    try {
      await api.putV1organisationcodeOrganisationCodeoptedInDocumentTypes(
        { optedInDocumentTypes: documentTypes.length ? documentTypes : null },
        { params: { organisationCode: selectedCode } },
      );

      // Update local state
      setCodeDataMap((prevMap) => {
        const newMap = new Map(prevMap);
        const existingData = newMap.get(selectedCode);
        if (existingData) {
          newMap.set(selectedCode, {
            ...existingData,
            optedInDocumentTypes: [...documentTypes],
          });
        }
        return newMap;
      });

      successNotificationHandler(OPTED_IN_DOCUMENT_TYPES_COPY.successToast);
    } catch (err) {
      errorNotificationHandler(backendReason(err) ?? OPTED_IN_DOCUMENT_TYPES_COPY.failureToast);
      throw err;
    }
  };

  return {
    selectedCode,
    setSelectedCode,
    isLoading,
    organisationCodes,
    selectedWorkflow,
    disabledWorkflows,
    handleWorkflowSwitch,
    initialReturnUrl,
    handleReturnUrlSave,
    initialRedirectAllowedOrigins,
    handleAllowedOriginsSave,
    availableOptedInDocumentTypes: AVAILABLE_OPTED_IN_DOCUMENT_TYPES,
    initialOptedInDocumentTypes,
    handleOptedInDocumentTypesSave,
  };
}
